import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { CommodityManagementApi, CommodityManagementMarketViewApi } from '../../../../../Apis/Apis';
import { CustomerReportModel, EmailScheduleModel, EmailScheduleSource, ReportType } from '../../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { StoneXDivider, StoneXRow } from '../../../../StoneX';
import { StoneXButton } from '../../../../StoneXMui';
import {
  createEmptyEmailScheduleDraft,
  describeEmailSchedule,
  emailScheduleDraftFromModel,
  hasEmailScheduleErrors,
  isValidEmail,
  toUpsertEmailScheduleRequest,
  validateEmailScheduleDraft,
} from '../../../EmailSchedules/EmailScheduleDraft';
import EmailScheduleFields from '../../../EmailSchedules/EmailScheduleFields';
import { EmailScheduleDraft } from '../../../../../Types/EmailScheduleDraft';

const EMAILABLE_REPORT_TYPES: ReportType[] = [
  ReportType.MarkToMarket,
  ReportType.Position,
  ReportType.ProfitAndLoss,
  ReportType.VarianceToBudget,
  ReportType.Summary,
  ReportType.Scenario,
  ReportType.Exposure,
  ReportType.DailyPositions,
  ReportType.PiedmontHedging,
];

export function reportTypeCanBeEmailed(reportType: ReportType): boolean {
  return EMAILABLE_REPORT_TYPES.includes(reportType);
}

interface ReportEmailScheduleSectionProps {
  report: CustomerReportModel;
}

export default function ReportEmailScheduleSection({ report }: ReportEmailScheduleSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const siteGuid = useApplicationSelector((state: RootState) => state.activeSite.site?.siteGuid);
  const userEmail = useApplicationSelector((state: RootState) => state.userAccess.email);

  const [existingSchedule, setExistingSchedule] = useState<EmailScheduleModel | null>(null);
  const [draft, setDraft] = useState<EmailScheduleDraft>(createEmptyEmailScheduleDraft());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  const errors = validateEmailScheduleDraft(draft);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    CommodityManagementMarketViewApi.listEmailSchedules(undefined, EmailScheduleSource.CommodityManagementReport, report.customerReportId)
      .then((response) => {
        if (cancelled) {
          return;
        }

        const schedule = response.data.rows?.[0] ?? null;
        setExistingSchedule(schedule);
        setDraft(emailScheduleDraftFromModel(schedule));
      })
      .catch((error) => {
        console.error('Failed to load the report email schedule', error);
        if (!cancelled) {
          setExistingSchedule(null);
          setDraft(createEmptyEmailScheduleDraft());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [report.customerReportId]);

  async function save() {
    if (!siteGuid) {
      enqueueSnackbar('Select a site before scheduling report emails.', { variant: 'warning' });
      return;
    }

    setIsSaving(true);

    try {
      if (draft.enabled) {
        const request = toUpsertEmailScheduleRequest(
          { ...draft, emailScheduleId: existingSchedule?.emailScheduleId ?? draft.emailScheduleId },
          report.customerReportId,
          EmailScheduleSource.CommodityManagementReport,
          siteGuid,
        );
        const response = await CommodityManagementMarketViewApi.upsertEmailSchedule(undefined, request);
        const saved = response.data.emailSchedule ?? null;
        setExistingSchedule(saved);
        setDraft(emailScheduleDraftFromModel(saved));
        enqueueSnackbar(`${report.reportName} will be emailed ${describeEmailSchedule(saved!).toLowerCase()}.`, { variant: 'success' });
      } else if (existingSchedule) {
        await CommodityManagementMarketViewApi.deleteEmailSchedule(undefined, String(existingSchedule.emailScheduleId));
        setExistingSchedule(null);
        setDraft(createEmptyEmailScheduleDraft());
        enqueueSnackbar(`${report.reportName} is no longer emailed on a schedule.`, { variant: 'success' });
      }
    } catch (error) {
      console.error('Failed to save the report email schedule', error);
      enqueueSnackbar('Failed to save the email schedule.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  async function sendTest() {
    const recipients = draft.recipients.filter(isValidEmail);
    const testRecipients = recipients.length > 0 ? recipients : userEmail ? [userEmail] : [];

    if (testRecipients.length === 0) {
      enqueueSnackbar('Add a recipient before sending a test.', { variant: 'warning' });
      return;
    }

    setIsSendingTest(true);

    try {
      const response = await CommodityManagementApi.sendCommodityManagementReportEmail(undefined, String(report.customerReportId), {
        customerReportId: report.customerReportId,
        recipients: testRecipients,
        subject: draft.subject.trim() === '' ? null : draft.subject.trim(),
      });
      enqueueSnackbar(`Sent "${response.data.subject}" to ${response.data.recipients?.join(', ')}.`, { variant: 'success' });
    } catch (error: any) {
      const detail = error?.response?.data?.detail ?? error?.message ?? 'unknown error';
      enqueueSnackbar(`The test email could not be sent: ${detail}`, { variant: 'error' });
    } finally {
      setIsSendingTest(false);
    }
  }

  const hasChanges = existingSchedule
    ? JSON.stringify(emailScheduleDraftFromModel(existingSchedule)) !== JSON.stringify(draft)
    : draft.enabled;

  return (
    <>
      <StoneXDivider />
      <StoneXRow>
        <h3>Email</h3>
      </StoneXRow>
      <StoneXRow>
        <Typography variant="body2" color="text.secondary">
          Email this report as an Excel workbook on a schedule. It runs against the previous business day and the site's default budget window.
        </Typography>
      </StoneXRow>

      <EmailScheduleFields draft={draft} errors={errors} disabled={isLoading || isSaving} onChange={setDraft} />

      {existingSchedule && (
        <StoneXRow>
          <Typography variant="caption" color={existingSchedule.lastRunError ? 'error' : 'text.secondary'}>
            {existingSchedule.lastRunError
              ? `Last run failed: ${existingSchedule.lastRunError}`
              : `Next run ${new Date(existingSchedule.nextRunAtUtc).toLocaleString()}${existingSchedule.lastRunAtUtc ? `, last sent ${new Date(existingSchedule.lastRunAtUtc).toLocaleString()}` : ''}`}
          </Typography>
        </StoneXRow>
      )}

      <StoneXRow justify="end" marginDirection="top">
        <StoneXButton variant="outlined" onClick={sendTest} isWorking={isSendingTest} disabled={isLoading || isSaving}>
          Send test email
        </StoneXButton>
        <StoneXButton onClick={save} isWorking={isSaving} disabled={isLoading || !hasChanges || hasEmailScheduleErrors(errors)}>
          Save email settings
        </StoneXButton>
      </StoneXRow>
    </>
  );
}
